import { useEffect, useRef, useState, type ReactElement } from 'react';
import { countTag, type ModDef, type OwnedMod } from '../sim/mods';

/* ═══════════════════════════════════════════════════════════════════════════
   Les écrans.

   Ils ne sont montés QUE hors du jeu (PLAN.md §3.7) : entrer en partie démonte
   l'arbre, ce qui ramène le coût runtime de React à exactement zéro pendant les
   16,67 ms qui comptent. Aucun de ces composants ne s'abonne à la boucle de
   jeu, et aucun ne lit un état qui change à 60 Hz.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Met le focus sur le premier élément interactif au montage.
 *
 *  Ce n'est pas un détail d'accessibilité : sans focus initial, la navigation
 *  clavier et manette ne démarre pas, et le critère « tout est atteignable à la
 *  manette » tombe dès le premier écran. */
function useAutoFocus<T extends HTMLElement>(): React.RefObject<T | null> {
  const ref = useRef<T>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return ref;
}

/* Règle tenue sur cet écran : un bouton n'y apparaît que le jour où il mène
   quelque part. OPTIONS y a été retiré tant que l'écran n'existait pas, parce
   qu'un bouton inerte fait conclure au joueur que le jeu est cassé, alors qu'il
   est seulement incomplet. */
export function TitleScreen({ onPlay, onEndless, onHangar, onRegistre, onOptions, best }: {
  onPlay: () => void;
  onEndless: () => void;
  onHangar: () => void;
  onRegistre: () => void;
  onOptions: () => void;
  best: number;
}): ReactElement {
  const ref = useAutoFocus<HTMLButtonElement>();
  return (
    <div className="screen">
      <h1>NEON DESCENT</h1>
      {best > 0 && <p>Meilleur score : {best.toLocaleString('fr-FR')}</p>}
      <button ref={ref} className="btn btn-primary" onClick={onPlay}>
        JOUER
      </button>
      {/* Le mode SANS FIN : pas de campagne, pas de boss — une pression qui monte
          avec le temps jusqu'à la mort. Un second pilier de rejouabilité à côté
          de la campagne, pour le score pur. */}
      <button className="btn" onClick={onEndless}>
        SANS FIN
      </button>
      <button className="btn" onClick={onHangar}>
        HANGAR
      </button>
      <button className="btn" onClick={onRegistre}>
        REGISTRE
      </button>
      <button className="btn" onClick={onOptions}>
        OPTIONS
      </button>
      <p>
        WASD, flèches ou souris pour piloter · Maj = focus · Espace = bombe
        <br />
        Clic gauche = arme principale · Clic droit = arme secondaire
        <br />
        Le tir principal est automatique. Le point blanc au centre de ton vaisseau est ta seule
        hitbox.
      </p>
    </div>
  );
}

export function PauseScreen({ onResume, onOptions, onQuit, onDev }: {
  onResume: () => void;
  onOptions: () => void;
  onQuit: () => void;
  /** Ouvre le menu développeur. Fourni UNIQUEMENT en dev (`import.meta.env.DEV`) :
   *  absent, le bouton ne s'affiche pas, donc rien en production. */
  onDev?: (() => void) | undefined;
}): ReactElement {
  const ref = useAutoFocus<HTMLButtonElement>();
  return (
    <div className="screen">
      <h2>PAUSE</h2>
      <button ref={ref} className="btn btn-primary" onClick={onResume}>
        REPRENDRE
      </button>
      {onDev && (
        <button className="btn btn-dev" onClick={onDev}>
          DÉVELOPPEUR
        </button>
      )}
      {/* Les options sont atteignables DEPUIS LA PAUSE, et pas seulement depuis
          le titre. C'est le moment où l'on découvre que la secousse gêne : la
          reléguer au menu principal obligerait à abandonner sa partie pour la
          régler, ce que personne ne fait — on subit. */}
      <button className="btn" onClick={onOptions}>
        OPTIONS
      </button>
      <button className="btn" onClick={onQuit}>
        ABANDONNER
      </button>
    </div>
  );
}

export interface SummaryData {
  readonly score: number;
  readonly kills: number;
  readonly graze: number;
  readonly level: number;
  readonly credits: number;
  readonly victory: boolean;
  /** Monde atteint, et longueur de la campagne. Un joueur qui meurt doit voir
   *  jusqu'où il est allé : « niveau 12 » ne dit rien de sa progression réelle,
   *  « monde 2 sur 3 » si. */
  readonly world: number;
  readonly worldCount: number;
  /** Présent UNIQUEMENT en mode sans fin. La campagne n'a pas de « temps de
   *  survie » qui vaille (elle se mesure en mondes) ; le mode sans fin, lui, se
   *  mesure entièrement là-dessus. Sa présence bascule le bilan sur cet axe. */
  readonly endless?: { readonly seconds: number; readonly stage: number } | undefined;
  /** Jalons de maîtrise décrochés PENDANT cette run. C'est le seul moment où
   *  ils peuvent être annoncés — ils ne retomberont jamais, et un joueur qui
   *  ne les voit pas ici ne saura jamais pourquoi il a gagné des points. */
  readonly milestones: ReadonlyArray<{ name: string; points: number }>;
}

/** Temps de survie « m:ss » pour le bilan du mode sans fin. */
function survie(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  return `${m}:${String(s - m * 60).padStart(2, '0')}`;
}

export function SummaryScreen({ data, onReplay, onHangar }: {
  data: SummaryData;
  onReplay: () => void;
  /* Le second bouton mène au HANGAR et non au titre. C'est là que les crédits
     et les points fraîchement gagnés deviennent quelque chose ; renvoyer au
     menu ferait disparaître la récompense avant que le joueur l'ait regardée.
     Le libellé doit dire où l'on va — « MENU » mentirait. */
  onHangar: () => void;
}): ReactElement {
  // ⚠️ REJOUER en focus par défaut. Critère du jalon : mort → nouveau run en
  // ≤ 3 entrées. Le bouton le plus probable doit être celui qu'on active en
  // appuyant simplement sur Entrée.
  const ref = useAutoFocus<HTMLButtonElement>();
  return (
    <div className="screen">
      {/* « CAMPAGNE TERMINÉE » et non « niveau terminé » : depuis l'enchaînement
          des mondes, la victoire signifie les avoir tous franchis. Annoncer un
          niveau réussi à qui vient de finir le jeu banaliserait le seul moment
          qui ne se reproduira pas. Le mode sans fin, lui, ne se gagne pas — il
          se survit. */}
      <h2>{data.endless ? 'SANS FIN' : data.victory ? 'CAMPAGNE TERMINÉE' : 'PARTIE TERMINÉE'}</h2>
      <dl className="stats">
        <dt>Score</dt>
        <dd>{Math.floor(data.score).toLocaleString('fr-FR')}</dd>
        <dt>Ennemis détruits</dt>
        <dd>{data.kills}</dd>
        <dt>Frôlements</dt>
        <dd>{data.graze}</dd>
        {data.endless ? (
          <>
            <dt>Temps de survie</dt>
            <dd>{survie(data.endless.seconds)}</dd>
            <dt>Palier atteint</dt>
            <dd>{data.endless.stage}</dd>
          </>
        ) : (
          <>
            <dt>Monde atteint</dt>
            <dd>
              {data.world} / {data.worldCount}
            </dd>
          </>
        )}
        <dt>Niveau atteint</dt>
        <dd>{data.level}</dd>
        <dt>Crédits gagnés</dt>
        <dd>{data.credits}</dd>
      </dl>

      {data.milestones.length > 0 && (
        <div className="milestones">
          <h3>JALONS DÉCROCHÉS</h3>
          {data.milestones.map((m) => (
            <div key={m.name} className="milestone">
              <span>{m.name}</span>
              <span className="ms-points">+{m.points} pts</span>
            </div>
          ))}
        </div>
      )}
      <button ref={ref} className="btn btn-primary" onClick={onReplay}>
        REJOUER
      </button>
      <button className="btn" onClick={onHangar}>
        HANGAR
      </button>
    </div>
  );
}

/** Décompte avant reprise (PLAN.md §6.12).
 *
 *  0,75 s après tout écran, avec le pattern gelé en fond. Sans lui, le joueur
 *  reprend le contrôle sur une balle qu'il ne suivait plus depuis dix secondes —
 *  et meurt d'une mort qu'il ne peut pas s'attribuer. */
function Countdown({ onDone, seconds = 0.75 }: { onDone: () => void; seconds?: number }): ReactElement {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (): void => {
      const e = (performance.now() - start) / 1000;
      const rest = seconds - e;
      if (rest <= 0) {
        onDone();
        return;
      }
      setLeft(rest);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone, seconds]);
  return <div className="countdown">REPRISE DANS {left.toFixed(1)} s</div>;
}

export function CardsScreen({ offer, owned, onPick, onReroll, canReroll, level }: {
  offer: readonly ModDef[];
  owned: readonly OwnedMod[];
  onPick: (def: ModDef) => void;
  onReroll: () => void;
  canReroll: boolean;
  level: number;
}): ReactElement {
  const ref = useAutoFocus<HTMLButtonElement>();
  const [picked, setPicked] = useState<ModDef | null>(null);

  if (picked) {
    return (
      <div className="screen">
        <h2>{picked.name}</h2>
        <div className="card-text">{picked.text}</div>
        <Countdown onDone={() => onPick(picked)} />
      </div>
    );
  }

  return (
    <div className="screen">
      <h2>NIVEAU {level}</h2>
      <div className="cards">
        {offer.map((def, i) => {
          const isFusion = Boolean(def.requires);
          return (
            <button
              key={def.id}
              ref={i === 0 ? ref : undefined}
              className={`card${isFusion ? ' fusion' : ''}`}
              onClick={() => setPicked(def)}
            >
              <div className="card-name">{def.name}</div>
              <div className="card-text">{def.text}</div>
              <div className="card-tags">
                {def.tags.map((t) => (
                  // Le compteur d'exemplaires par tag est ce qui transforme une
                  // liste de bonus en BUILD : le joueur voit sa voie progresser
                  // et peut viser une Fusion.
                  <span key={t} className={countTag(owned, t) > 0 ? 'tag owned' : 'tag'}>
                    {t} {countTag(owned, t) > 0 ? countTag(owned, t) : ''}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
      {canReroll && (
        <button className="btn" onClick={onReroll}>
          RELANCER
        </button>
      )}
    </div>
  );
}
